import React, { Fragment, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    FormLabelComposer,
    TextInput,
    FormErrorComposer,
} from '@fluid/form-elements';
import { Button } from '@fluid/buttons';
import {
    getDataAttributes,
    getCSSClasses,
    getLocaleText,
    getInputValue,
} from '../../utils/LoginUtils';
import LoginContext from '../../context/LoginContext';

const TextInputWithLabel = FormLabelComposer(FormErrorComposer(TextInput));

const BASE_CLS = 'password-field';
const SHOW_HIDE_ENABLED = 'show-hide-enabled';
const PasswordField = (props) => {
    const {
        loginConfig,
        isInteractive,
        locale,
        onPasswordChange,
        password,
    } = props;

    const [showPassword, setShowPassword] = useState(false);
    const { context, setContext } = useContext(LoginContext);

    const { passwordGroup } = loginConfig ?? {};
    const { passwordLabel, isShowPasswordEnabled, cssClasses, dataAttributes } =
        passwordGroup ?? {};

    const labelText = getLocaleText(passwordLabel, locale);

    const handleShowHideClick = () => {
        setShowPassword(!showPassword);
    };
    const handleChange = (e) => {
        const value = getInputValue(e);
        setContext({ ...context, invalidPassword: !value });

        if (onPasswordChange) {
            onPasswordChange(value);
        }
    };

    // Defining callsign keystroke field using css class
    // https://docs.callsign.com/docs/web-intel-sdk-registered/287a7228a1bcb-setting-up-the-web-sdk#defining-the-keystroke-fields
    useEffect(() => {
        const passwordInput = document.querySelector('#password');

        if (passwordInput) {
            passwordInput.classList.add('cs-password');
        }
    }, []);

    // Callsign web SDK depends on password field to have keystroke css class.
    // Intimate the host page that the widget is loaded.
    useEffect(() => {
        context?.onWidgetInit?.();
    }, []);

    const passwordProps = {
        labelText,
        name: 'password',
        autoComplete: 'off',
        // id, autoCapitalize, autoCorrect and spellCheck attributes are must for callsign data collection to work
        id: 'password',
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellcheck: 'false',
        isStacked: true,
        contextCls: `${getCSSClasses(BASE_CLS, cssClasses)} ${
            isShowPasswordEnabled ? SHOW_HIDE_ENABLED : ''
        }`,
        value: password,
        ...getDataAttributes(dataAttributes),
    };
    const showHideProps = {
        iconCls: showPassword ? 'fluid-icon-eye-slash' : 'fluid-icon-eye',
    };

    passwordProps.isProtected = !showPassword;

    if (isInteractive) {
        passwordProps.onChange = handleChange;
        showHideProps.onClick = handleShowHideClick;
    } else {
        passwordProps.tabIndex = -1;
        passwordProps.isReadOnly = true;

        showHideProps.tabIndex = -1;
    }

    return (
        <Fragment>
            <TextInputWithLabel {...passwordProps} />
            {isShowPasswordEnabled && (
                <div className={`${BASE_CLS}__show-hide`}>
                    <Button {...showHideProps} />
                </div>
            )}
        </Fragment>
    );
};

PasswordField.defaultProps = {};

PasswordField.propTypes = {
    locale: PropTypes.string.isRequired,
    password: PropTypes.string,
    loginConfig: PropTypes.object.isRequired,
    isInteractive: PropTypes.bool,
    onPasswordChange: PropTypes.func,
};

export default PasswordField;
